import {
  type GuildRecord,
  guildIconUrl,
  selectGuilds,
  selectGuildUnread,
  selectMentionCount,
  selectUnreadPrivateChannels,
} from '@alternativamais/chat-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../componentes/Avatar';
import { abrirCamada } from '../../chat/camadas';
import { Icone } from '../../componentes/Icone';
import { useDerivado, useEstado } from '../../chat/contexto';
import { useNome } from '../../chat/ganchos';
import { ARQUIVOS } from '../../config';
import { iniciais } from '@alternativamais/chat-core';
import { rotaDoServidor } from './rotas';
import { noTamanho } from '../../componentes/imagens';

/** Pílula à esquerda: selecionado (alta), passando o mouse (média), não lido (baixa). */
function Pilula({ altura }: { altura: number }) {
  if (!altura) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute top-1/2 left-0 w-1 -translate-y-1/2 rounded-r bg-texto transition-[height]"
      style={{ height: altura }}
    />
  );
}

function Contador({
  valor,
  fundo = 'var(--cor-rail)',
}: {
  valor: number;
  fundo?: string;
}) {
  if (!valor) return null;
  return (
    <span
      className="absolute -right-1 -bottom-1 flex h-5 min-w-5 items-center justify-center rounded-[10px] bg-perigo px-[5px] text-[11px] font-bold text-white"
      style={{ border: `3px solid ${fundo}` }}
    >
      {valor > 99 ? '99+' : valor}
    </span>
  );
}

/** Barra de servidores (72 px): início com as DMs não lidas e os servidores. */
export function Rail({ servidorAtual }: { servidorAtual: string | null }) {
  const guilds = useDerivado(selectGuilds, (estado) => [
    estado.guildIds,
    estado.guilds,
    estado.userSettings,
  ]);
  const dmsNaoLidas = useDerivado(selectUnreadPrivateChannels, (estado) => [
    estado.privateChannelIds,
    estado.channels,
    estado.readStates,
    estado.guildSettings,
  ]);
  const navegar = useNavigate();
  const [passando, setPassando] = useState<string | null>(null);
  return (
    <nav
      aria-label="Servidores"
      className="rolagem-escondida flex w-[72px] shrink-0 flex-col items-center gap-2 overflow-y-auto bg-rail py-3"
    >
      <div className="relative flex h-12 w-[72px] shrink-0 justify-center">
        <Pilula
          altura={servidorAtual === null ? 40 : passando === '@me' ? 20 : 0}
        />
        <button
          type="button"
          aria-label="Início e mensagens diretas"
          title="Mensagens diretas"
          onMouseEnter={() => setPassando('@me')}
          onMouseLeave={() => setPassando(null)}
          onClick={() => navegar('/canais/@me')}
          className={`flex size-12 items-center justify-center rounded-[14px] transition-colors ${
            servidorAtual === null
              ? 'bg-destaque text-destaque-texto'
              : 'bg-chat text-texto-2 hover:bg-destaque hover:text-destaque-texto'
          }`}
        >
          <Icone nome="logo" tamanho={26} />
        </button>
      </div>
      {dmsNaoLidas.map((canal) => (
        <DmNaoLida key={canal.id} canalId={canal.id} />
      ))}
      <div className="h-0.5 w-8 shrink-0 rounded-sm bg-hover" />
      {guilds.map((guild) => (
        <Servidor
          key={guild.id}
          guild={guild}
          ativo={guild.id === servidorAtual}
          passando={passando === guild.id}
          aoPassar={(sim) => setPassando(sim ? guild.id : null)}
          aoAbrir={() => navegar(rotaDoServidor(guild.id))}
        />
      ))}
      <button
        type="button"
        aria-label="Adicionar um servidor"
        title="Adicionar um servidor"
        onClick={() => abrirCamada({ tipo: 'criar-servidor' })}
        className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-chat text-online transition-[border-radius,background] hover:rounded-[14px] hover:bg-online hover:text-white"
      >
        <Icone nome="mais" tamanho={24} />
      </button>
    </nav>
  );
}

function Servidor({
  guild,
  ativo,
  passando,
  aoPassar,
  aoAbrir,
}: {
  guild: GuildRecord;
  ativo: boolean;
  passando: boolean;
  aoPassar: (sim: boolean) => void;
  aoAbrir: () => void;
}) {
  const naoLido = useEstado(
    (estado) => selectGuildUnread(estado, guild.id).unread,
  );
  const mencoes = useEstado(
    (estado) => selectGuildUnread(estado, guild.id).mentions,
  );
  const icone = guildIconUrl(ARQUIVOS, guild);
  const rotulo = `${guild.name}${
    mencoes
      ? `, ${mencoes} ${mencoes === 1 ? 'menção' : 'menções'}`
      : naoLido
        ? ', mensagens não lidas'
        : ''
  }`;
  return (
    <div className="relative flex h-12 w-[72px] shrink-0 justify-center">
      <Pilula altura={ativo ? 40 : passando ? 20 : naoLido ? 8 : 0} />
      <button
        type="button"
        aria-label={rotulo}
        aria-current={ativo ? 'page' : undefined}
        title={guild.name}
        onMouseEnter={() => aoPassar(true)}
        onMouseLeave={() => aoPassar(false)}
        onClick={aoAbrir}
        className={`relative flex size-12 items-center justify-center rounded-[14px] font-titulo text-[17px] font-bold ${
          icone
            ? ''
            : ativo
              ? 'bg-destaque text-destaque-texto'
              : 'bg-chat text-texto hover:bg-destaque hover:text-destaque-texto'
        }`}
      >
        {icone ? (
          <img
            src={noTamanho(icone, 48)}
            alt=""
            className="size-12 rounded-[14px] object-cover"
          />
        ) : (
          iniciais(guild.name).slice(0, 2)
        )}
        <Contador valor={mencoes} />
      </button>
    </div>
  );
}

function DmNaoLida({ canalId }: { canalId: string }) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  const mencoes = useEstado((estado) => selectMentionCount(estado, canalId));
  const navegar = useNavigate();
  const pessoa = canal?.recipients?.[0];
  const nome = useNome(pessoa?.id ?? '', null, pessoa);
  if (!canal || !pessoa) return null;
  const titulo = canal.name || nome;
  return (
    <div className="relative flex h-12 w-[72px] shrink-0 justify-center">
      <Pilula altura={8} />
      <button
        type="button"
        aria-label={`${titulo}, ${mencoes || 'mensagens'} não lidas`}
        title={titulo}
        onClick={() => navegar(`/canais/@me/${canalId}`)}
        className="relative rounded-full"
      >
        <Avatar pessoa={pessoa} nome={titulo} tamanho={48} />
        <Contador valor={mencoes} />
      </button>
    </div>
  );
}
